import React, { useEffect, useMemo, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import { decodeJson, encodeJson, StringParam, withDefault } from 'use-query-params';
import isEqual from 'lodash/isEqual';

import type { ELeadStage } from 'src/services/api';
import type { IConversation } from 'src/api/landlord-messages-api';
import { useConversations, useProperties, useUserSettings } from 'src/services/api';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import {
    type TLandlordConversationsContext,
    EConversationTab,
    conversationTabs,
    initialConversationsPagesByCategory,
    conversationCategories,
    EConversationCategory,
    type IConversationMatchWithChannel,
} from 'src/pages/landlord/conversations/context/types';
import { useOpenConversation } from 'src/pages/landlord/conversations/context/hooks';
import { LandlordConversationsContext } from 'src/pages/landlord/conversations/context/context';
import { filterConversations } from 'src/pages/landlord/conversations/utils/filter-conversations';
import { getConversationsByCategory } from 'src/pages/landlord/conversations/utils/get-conversations-by-category';
import EmptyConversationsList from 'src/pages/landlord/conversations/components/empty/EmptyConversationsList';
import { useTypedQueryParam } from 'src/hooks/use-typed-query-param';
import useCurrentInbox from 'src/services/api/inboxes/use-current-inbox';

export const INITIAL_FILTERS: {
    assigneeIds: string[];
    ingestionSources: string[];
    leadStages: ELeadStage[];
    leadLabelIds: string[];
    excludeLeadLabelIds: string[];
    propertyIds: string[];
    hasNoProperty: boolean;
    propertyLabelIds: string[];
} = {
    assigneeIds: [],
    ingestionSources: [],
    leadStages: [],
    leadLabelIds: [],
    excludeLeadLabelIds: [],
    propertyIds: [],
    hasNoProperty: false,
    propertyLabelIds: [],
};
const ROWS_PER_PAGE = 25;

type Props = {
    children: React.ReactNode;
};

export default function LandlordConversationsProvider({ children }: Props) {

    const [isFilterOpen, setFilterOpen] = useState(false);
    const [selectedConversations, setSelectedConversations] = useState<(IConversation | IConversationMatchWithChannel)[]>([]);
    const [focusedConversation, setFocusedConversation] = useState<IConversation>();
    const [pagesByCategory, setPagesByCategory] = useState(initialConversationsPagesByCategory);
    const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<HTMLElement>();

    const { loggedInUser, showArchived, leads } = useAuthenticatedState();
    const { data: userSettings } = useUserSettings();
    const { data: conversations } = useConversations();
    const { data: properties } = useProperties();
    const { inbox } = useCurrentInbox();

    const [tab, setTab] = useTypedQueryParam<EConversationTab>('tab', {
        encode: (t) => t,
        decode: (s) => {
            if (conversationTabs.includes(s as EConversationTab)) {
                return s as EConversationTab;
            }
            return EConversationTab.ALL;
        }
    });


    const [search, setSearch] = useTypedQueryParam<string>('search', withDefault(StringParam, ''));
    const [filter, _setFilter] = useTypedQueryParam<any>('filter', {
        encode: (value) => {
            if (value == null) {
                return value;
            }
            if (typeof value === 'object') {
                const withoutDefaults = Object.entries(value).reduce((acc, [key, val]) => {
                    if (key in INITIAL_FILTERS && isEqual(INITIAL_FILTERS[key as keyof typeof INITIAL_FILTERS], val)) {
                        return acc;
                    }
                    // ts actually pretty stupid, we have type cast and check just before and AST ignores that
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    acc[key] = val;
                    return acc;
                }, {} as Partial<typeof INITIAL_FILTERS>);
                return encodeJson(withoutDefaults);
            }
            return encodeJson(value);
        },
        decode: (value) => {
            const decoded = decodeJson(value);
            if (typeof decoded === 'object' && decoded != null) {
                return {
                    ...INITIAL_FILTERS,
                    ...decoded,
                };
            }
            return INITIAL_FILTERS;
        },
    });

    const setFilter = (newFilter: Partial<typeof INITIAL_FILTERS>) => {
        const _newFilter = { ...filter, ...newFilter };
        _setFilter(_newFilter);
    };
    useEffect(() => {
        if (inbox && isEqual(filter, INITIAL_FILTERS)) {
            setFilter(inbox.filters);
        }
    }, [inbox]);


    const hiddenLeadLabelIds = userSettings?.conversations_hidden_lead_label_ids ?? undefined;

    const leadsByUuid = useMemo(() => {
        if (!leads) {
            return new Map();
        }

        return new Map(leads.map(o => [o.uuid, o]));
    }, [leads]);

    const teamMembers = useMemo(() => {
        if (!loggedInUser?.leasing_team_members) {
            return [];
        }

        return loggedInUser.leasing_team_members;
    }, [loggedInUser]);

    const resetFilter = () => {
        _setFilter(INITIAL_FILTERS);
    };

    // Reset selected conversations when the tab, search or filter changes
    useEffect(() => {
        setSelectedConversations([]);
        setFocusedConversation(undefined);
    }, [tab, search, filter]);

    useEffect(() => {
        if (conversations === undefined) {
            return;
        }

        const conversationsById = new Map(conversations.map(o => [o.lead_uuid, o]));
        setSelectedConversations(prev => {
            return prev.map(o => {
                return conversationsById.get(o.lead_uuid) || o;
            });
        });
    }, [conversations]);

    const filteredConversations = useMemo(() => {
        if (!filter) {
            return conversations;
        }

        return filterConversations({
            hiddenLeadLabelIds,
            properties,
            leadsByUuid,
            conversations,
            tab,
            search,
            filter
        });
    }, [properties, leadsByUuid, conversations, tab, search, filter]);

    const conversationsByTabs = useMemo(() => {
        return conversationTabs.reduce((acc, _tab) => {
            let _filteredConversations = conversations;
            if (filter) {
                _filteredConversations = filterConversations({
                    hiddenLeadLabelIds,
                    properties,
                    leadsByUuid,
                    conversations,
                    tab: _tab,
                    search,
                    filter
                });
            }

            return {
                ...acc,
                [_tab]: _filteredConversations,
            };
        }, {} as Record<EConversationTab, IConversation[]>);
    }, [properties, leadsByUuid, conversations, search, filter]);

    const categories = useMemo(() => {
        if (showArchived) {
            return conversationCategories;
        } else {
            return conversationCategories.filter(o => o !== EConversationCategory.Archived);
        }
    }, [showArchived]);

    const conversationsByCategory = useMemo(() => {
        return conversationCategories.reduce((acc, cat) => {
            if (!leads) {
                return {
                    ...acc,
                    [cat]: [],
                };
            }

            const tabConversations = conversationsByTabs[tab]?.map(conversation => ({
                ...conversation,
                is_archived: leads.find(lead => lead.uuid === conversation.lead_uuid)?.is_archived || false,
            })) ?? [];

            return {
                ...acc,
                [cat]: getConversationsByCategory(tabConversations, cat)
            };
        }, {} as TLandlordConversationsContext['conversationsByCategory']);
    }, [tab, conversationsByTabs, leads]);

    const visibleConversationsByCategory = useMemo(() => {
        return conversationCategories.reduce((acc, cat) => {
            const _conversations = conversationsByCategory[cat];
            const _page = pagesByCategory[cat];
            return {
                ...acc,
                [cat]: _conversations.slice((_page - 1) * 25, _page * 25),
            };
        }, {} as TLandlordConversationsContext['conversationsByCategory']);
    }, [conversationsByCategory, pagesByCategory]);

    const visibleConversations = useMemo(() => {
        return categories
            .map(cat => visibleConversationsByCategory[cat])
            .flat();
    }, [categories, visibleConversationsByCategory]);

    // Reset pages when conversations change
    useEffect(() => {
        setPagesByCategory(initialConversationsPagesByCategory);
    }, [setPagesByCategory, filteredConversations ? filteredConversations.map(o => o.lead_uuid).join(',') : null]);

    const openConversation = useOpenConversation(conversationsByCategory, leadsByUuid);

    const isLoading = !loggedInUser ||
        !leads ||
        !properties ||
        !conversations ||
        !tab;
    if (isLoading) {
        return (
            <LinearProgress sx={{ position: 'fixed', top: 0 }} />
        );
    }

    const hasConversations = !!conversations.length;

    const isSearchOrFilterActive = !!search ||
        !!filter?.assigneeIds.length ||
        !!filter?.ingestionSources.length ||
        !!filter?.leadStages.length ||
        !!filter?.leadLabelIds.length ||
        !!filter?.propertyIds.length ||
        !!filter?.propertyLabelIds.length;

    return (
        <LandlordConversationsContext.Provider value={{
            loggedInUser,
            teamMembers,
            leads,
            leadsByUuid,
            properties,

            conversations,
            filteredConversations,

            tab,
            setTab,
            conversationsByTabs,

            search,
            setSearch,
            isFilterOpen,
            setFilterOpen,
            filter,
            setFilter,
            resetFilter,
            isSearchOrFilterActive,

            rowsPerPage: ROWS_PER_PAGE,

            categories,
            pagesByCategory,
            setPagesByCategory,
            conversationsByCategory,
            visibleConversationsByCategory,
            visibleConversations,

            selectedConversations,
            setSelectedConversations,
            focusedConversation,
            setFocusedConversation,

            openConversation,

            mobileMenuAnchorEl,
            setMobileMenuAnchorEl,
        }}
        >
            {hasConversations && children}
            {!hasConversations && <EmptyConversationsList />}
        </LandlordConversationsContext.Provider>
    );
}
